import { useEffect } from "react";
import { seo, company } from "@/config/site";

/**
 * Lightweight SEO helper. Sets document.title and select meta tags per route.
 * No react-helmet dependency. Works with any static host.
 */
export default function SEO({ title, description, canonical, image, keywords, jsonLd }) {
  useEffect(() => {
    const fullTitle = title ? seo.titleTemplate.replace("%s", title) : seo.defaultTitle;
    document.title = fullTitle;

    upsertMeta("description", description || seo.defaultDescription);
    upsertMeta("keywords", (keywords || []).join(", "));
    upsertLink("canonical", canonical || window.location.href);

    upsertMeta("og:site_name", company.brandName, true);
    upsertMeta("og:title", fullTitle, true);
    upsertMeta("og:description", description || seo.defaultDescription, true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:url", canonical || window.location.href, true);
    upsertMeta("og:image", image || seo.ogImage, true);

    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", fullTitle);
    upsertMeta("twitter:description", description || seo.defaultDescription);

    if (seo.googleSiteVerification) {
      upsertMeta("google-site-verification", seo.googleSiteVerification);
    }

    let ldTag = document.getElementById("__seo_jsonld");
    if (jsonLd) {
      if (!ldTag) {
        ldTag = document.createElement("script");
        ldTag.type = "application/ld+json";
        ldTag.id = "__seo_jsonld";
        document.head.appendChild(ldTag);
      }
      ldTag.textContent = JSON.stringify(jsonLd);
    } else if (ldTag) {
      ldTag.remove();
    }
  }, [title, description, canonical, image, keywords, jsonLd]);

  return null;
}

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content || "");
}

function upsertLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}
